import React from 'react';
import { Building2, Sparkles, X } from 'lucide-react';
import { SERVICE_LABELS, levelLabel, applyEntityToEntry } from './mlsServiceTypes';

const MODE_HINT = {
  auto: 'Auto-populated from the Jurisdiction Directory (routing rule). You can change it.',
};

/* Entity chooser panel (MLS-SVC-DIR-001 / MLS-SVC-UX-001). Never guesses: without a
   routing rule the single available entity is only suggested. Selecting an entity
   writes an order-level snapshot only — master directory data is never touched.
   MLS-ENTITY-LOCK: no Unlink — a wrong match is fixed via Change Entity / Request New Entity. */
export const MlsEntitySelector = ({ def, entry, service, serviceType, index, disabled, onChange, onClose }) => {
  if (!service) return null;
  const { mode, candidates = [], suggested, note } = service;
  const linked = !!entry.service_entity_id;
  const prefix = `mls-${def.key}-entry-${index}`;

  const choose = (id) => {
    const ent = candidates.find((c) => c.id === id);
    if (ent) { onChange(applyEntityToEntry(entry, def, ent)); onClose?.(); }
  };

  const byLevel = candidates.reduce((acc, c) => { (acc[c.jurisdiction_level] ||= []).push(c); return acc; }, {});
  const groups = [['municipality', 'City / Municipality'], ['county', 'County'], ['other', 'Other']].filter(([k]) => byLevel[k]?.length);
  const multiLevel = groups.length > 1;

  return (
    <div className="mls-reveal rounded-lg border border-blue-200 bg-blue-50/60 px-3 py-2.5" data-testid={`${prefix}-entity-selector`} data-mode={mode} data-multi-level={multiLevel}>
      <div className="flex items-center gap-2 flex-wrap">
        <Building2 className="w-3.5 h-3.5 text-blue-600 flex-shrink-0" />
        <span className="text-[10px] font-semibold text-blue-800 uppercase tracking-wider">{linked ? 'Change' : 'Select'} {SERVICE_LABELS[serviceType]} Entity</span>
        {!disabled && candidates.length > 0 && (
          <select value={entry.service_entity_id || ''} onChange={(e) => choose(e.target.value)}
            className="mls-field ml-auto h-7 max-w-[360px] px-2 text-xs rounded-md border border-blue-300 bg-white focus:outline-none focus:border-blue-500"
            data-testid={`${prefix}-entity-select`}>
            <option value="">{linked ? 'Change entity…' : `Select ${SERVICE_LABELS[serviceType]} authority…`}</option>
            {groups.map(([k, title]) => (
              <optgroup key={k} label={title}>
                {byLevel[k].map((c) => <option key={c.id} value={c.id}>{c.entity_name} — {levelLabel(c)}{c.is_default ? ' (default)' : ''}</option>)}
              </optgroup>
            ))}
          </select>
        )}
        {onClose && (
          <button type="button" onClick={onClose} className="h-6 w-6 grid place-items-center rounded text-blue-400 hover:text-blue-700 hover:bg-blue-100 transition-colors"
            title="Close" data-testid={`${prefix}-entity-selector-close`}><X className="w-3.5 h-3.5" /></button>
        )}
      </div>
      {!disabled && mode === 'suggest' && suggested && !linked && (
        <button type="button" onClick={() => choose(suggested.id)}
          className="mt-2 w-full flex items-center gap-2 rounded-md border border-blue-300 bg-white px-2.5 py-1.5 text-left hover:bg-blue-100/60 transition-colors"
          data-testid={`${prefix}-entity-suggested`}>
          <Sparkles className="w-3.5 h-3.5 text-blue-600 flex-shrink-0" />
          <span className="text-xs text-slate-800"><span className="font-semibold">Use {suggested.entity_name}</span> <span className="text-slate-500">— {levelLabel(suggested)} · {suggested.jurisdiction_name}</span></span>
          <span className="ml-auto text-[10px] font-bold text-blue-700 uppercase tracking-wide">Select</span>
        </button>
      )}
      {(note || MODE_HINT[mode]) && (
        <p className="mt-1.5 text-[10.5px] text-slate-600" data-testid={`${prefix}-entity-note`}>{linked && mode === 'auto' ? MODE_HINT.auto : note}</p>
      )}
    </div>
  );
};
